/**
 * Maschinentypen-Verwaltung - Liste
 * 
 * Funktionen:
 * - Liste aller Maschinentypen
 * - Navigation zu Detailseite (Create/Edit)
 * 
 * UX-Leitlinie: Keine Modals für Create/Edit, nur Liste → Detailseite
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cog, Plus, RefreshCw, ChevronRight } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface MachineType {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

const sortAlpha = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, 'de', { sensitivity: 'base' }));
};

export default function MachineTypesPage() {
  const navigate = useNavigate();
  const [types, setTypes] = useState<MachineType[]>([]);
  const [loading, setLoading] = useState(true);

  // Load types
  const loadTypes = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/machine-types', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTypes(data);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Maschinentypen:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadTypes();
  }, []);

  // Filter active + alphabetisch sortieren
  const filtered = sortAlpha(types.filter(t => t.isActive !== false));

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Cog className="w-6 h-6" />
              Maschinentypen
            </h1>
            <p className="text-sm text-slate-500 mt-1">{filtered.length} aktive Typen</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadTypes}
              disabled={loading}
              className="p-2 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              title="Aktualisieren"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button 
              onClick={() => navigate('/admin/machine-types/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Neuer Typ
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Laden...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Keine Maschinentypen vorhanden
              <button 
                onClick={() => navigate('/admin/machine-types/new')}
                className="block mx-auto mt-4 text-blue-600 hover:underline"
              >
                Ersten Typ anlegen
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Name</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map(t => (
                  <tr 
                    key={t.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/admin/machine-types/${t.id}`)}
                  >
                    <td className="px-4 py-3 font-medium">{t.name}</td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
